(ns hai.ui.tokens
  "Design tokens"
  {:ornament/prefix ""}
  #?(:cljs (:require-macros [hai.ui.tokens :refer [def-alpha-range-props]]))
  (:require
   [garden.color :as gc]
   [lambdaisland.ornament :as o]))

(def ornament-tokens
  {:colors {:primary "1E005D"
            :primary-faded "4705D0"
            :muted-light "F9FAFB"
            :muted "667085"
            :muted-dark "344054"
            :faded "667085"
            :faded-light "A8B0BE"
            :blue {50 "DCF0FF"}
            :gray {200 "E8EBEF"
                   300 "D0D5DD"
                   500 "667085"
                   700 "344054"}}
   :tw-version 3})

(o/defprop --ask-color "#FFFAEB")
(o/defprop --mood-color "#F3E3FA")
(o/defprop --entry-color "#F1F9FF")
(o/defprop --humble-blue "#3563fa")
(o/defprop --light-humble-blue "#63bdff")

;; Calm, sage secondary-link color — for clickable affordances that shouldn't
;; carry the loud primary/CTA weight.
(o/defprop --link-muted "#6B7560")

(o/defprop --humble-offwhite "#F8F3F0")
(o/defprop --humble-primary "#4705D0")
(o/defprop --humble-primary-dark "#1E005D")
(o/defprop --humble-primary-light "#A183FF")
(o/defprop --humble-secondary "#FEBBCC")

(o/defprop --gray-0 "#f8f9fa")
(o/defprop --gray-1 "#f1f3f5")
(o/defprop --gray-2 "#e9ecef")
(o/defprop --gray-3 "#dee2e6")
(o/defprop --gray-4 "#ced4da")
(o/defprop --gray-5 "#adb5bd")
(o/defprop --gray-6 "#868e96")
(o/defprop --gray-7 "#495057")
(o/defprop --gray-8 "#343a40")
(o/defprop --gray-9 "#212529")
(o/defprop --gray-10 "#16191d")
(o/defprop --gray-11 "#0d0f12")
(o/defprop --gray-12 "#030507")

;; Added during Donavan's implementation of the design
;; See: https://docs.google.com/document/d/1xkIO5dgLnFFFmDVnq8JTKUvC-AtaWmjFnAbA9B1oqbQ/edit?tab=t.xpkrgzzgisr6

(o/defprop --font-family "'Manrope', sans-serif")

(defn opacify
  [hex opacity]
  (let [c (-> (gc/as-hsla hex)
              (assoc :alpha 0)
              (gc/opacify opacity))]
    (str "hsl("
         (int (:hue c)) ", "
         (int (:saturation c)) "%, "
         (int (:lightness c)) "%, "
         (:alpha c)
         ")")))

#?(:clj
   (defmacro def-alpha-range-props
     "Given a symbol and a hex value, creates an Ornament defprop for the given symbol and value
      plus 10 more variations from 10% to 100% (just for completeness, 100% is the same as the defined
      prop) opacity with the percentage suffixed to the symbol name, e.g...:

      (def-alpha-range-props --text-secondary \"#1D2939\")

      ...evaluates to essentially something like this:

      (do
        (o/defprop --text-secondary-10 (opacify \"#1D2939\" 0.1))
        (o/defprop --text-secondary-20 (opacify \"#1D2939\" 0.2))
        ...
        (o/defprop --text-secondary-90 (opacify \"#1D2939\" 0.9))
        (o/defprop --text-secondary-100 (opacify \"#1D2939\" 1.0))
        (o/defprop --text-secondary \"#1D2939\"))"
     [sym hex]
     (-> `()
         (conj `(o/defprop ~sym ~hex))
         (into
          (map (fn [i]
                 (let [symbol-i (symbol (str (name sym) "-" (* i 10)))
                       opacity (double (/ i 10))]
                   `(o/defprop ~symbol-i (opacify ~hex ~opacity)))))
          (range 10 0 -1))
         (conj 'do))))

;; White
(def white "#FFFFFF")
(o/defprop --colour-white white)
(o/defprop --bg-1 white)

;; Ink
(def ink "#1E005D")
(o/defprop --colour-ink ink)
(o/defprop --hai-dark-purple ink)
(o/defprop --text-primary ink)

;; Purple
(def purple "#4705D0")
(def-alpha-range-props --colour-purple purple)
(o/defprop --hai-purple-2 purple)
(o/defprop --text-primary-em purple)

;; Paper
(def paper "#F5F5F3")
(o/defprop --colour-paper paper)
(o/defprop --bg-2 paper)

;; Charcoal
(def charcoal "#1D2939")
(def-alpha-range-props --charcoal charcoal)
(def-alpha-range-props --text-secondary charcoal)

;; Pink

(def pink "#FEBBCC")
(o/defprop --colour-pink pink)
(o/defprop --text-secondary-em pink)

;; Snow

(def snow "#FDFEFC")
(o/defprop --colour-snow snow)
(o/defprop --hai-white-1 snow)

;; Chalk
(def chalk "#F6F5F0")
(o/defprop --colour-chalk chalk)
(o/defprop --hai-white-2 chalk)

;; Stone
(o/defprop --colour-stone "#F8F3F0")

;; Rock
(o/defprop --colour-rock "#DAD2CE")

;; Dim — the darkest of the warm neutrals (Chalk → Stone → Rock → Dim), and the
;; design system's colour for muted and helper text. It is the lightest warm
;; neutral that still clears 4.5:1 on Snow and Chalk, so Rock and above are for
;; surfaces and borders, never for text.
(def dim "#655C56")
(o/defprop --colour-dim dim)

;; Yellow
(o/defprop --colour-yellow "#FFC600")

;; Mint
(o/defprop --colour-mint "#FFC600")

;; 20% tints, plus a mint-family ink that passes WCAG AA on Mint-20.
;; Used by the Connections-page setup strip.
(o/defprop --colour-pink-20 "#FFF1F5")
(o/defprop --colour-mint-20 "#DAF8F3")
(o/defprop --colour-mint-ink "#0D6B55")

;; Blue
(o/defprop --colour-blue "#FFC600")

;; Sky
(o/defprop --colour-sky "#FFC600")

;; Raspberry
(def rasberry "#E01E5A")
(def-alpha-range-props --colour-rasberry rasberry)

;; Violet
(o/defprop --colour-violet "#A183FF")

;; Pastel yellow (notes)
(o/defprop --colour-pastel-yellow "#f6e89a")
(o/defprop --notes "#f6e89a")

;; Sage green (tasks)
(def sage-green "#d6e2bf")
(o/defprop --colour-sage-green sage-green)
(o/defprop --tasks sage-green)

;; Pastel blue
(o/defprop --colour-pastel-blue "#9ae0f6")
(o/defprop --contact "#9ae0f6")

;; Soft pink (calendar) — a low-saturation tint sibling to --notes/--tasks, not
;; the intense brand --humble-secondary. --calendar is the header; --calendar-light
;; is the softer body/row fill (mirrors --sticky-yellow / --sticky-yellow-light).
(o/defprop --colour-pastel-pink "#f7dae3")
(o/defprop --calendar "#f7dae3")
(o/defprop --calendar-light "#fbe8ef")

;; Soft sky-blue (My Progress) — a low-saturation tint sibling to
;; --notes/--tasks/--calendar. Desaturated echo of the progress timeline's own
;; sky event lane (#A8C9D6), so the header pulls from a colour already in the card.
(o/defprop --colour-pastel-sky "#c3d9e8")
(o/defprop --progress "#c3d9e8")

;; Kanban lane washes — one colour, three strengths: the tasks tint the mini-app
;; card wears, dimmed with [[opacify]] so the page shows through. Now holds it
;; nearly full, Later is barely there, and the column a task sits in reads as
;; how much of the tasks colour it has earned. One hue keeps the board calm and
;; leaves violet — what a lane wears while a card is dragged over it — the only
;; other colour a lane ever takes.
(o/defprop --lane-now   (opacify sage-green 0.85))
(o/defprop --lane-next  (opacify sage-green 0.55))
(o/defprop --lane-later (opacify sage-green 0.30))

;; Task accent green — link text, deadline label, quick-add pill (+ hover)
(o/defprop --task-accent "#5B6F4E")
(o/defprop --task-accent-hover "#3F5236")

;; END Added during Donavan's implementation of the design

;; ── Card palette ────────────────────────────────────────────────────────────
;; Used by the entity-card components (mini-apps, connections, integrations)
;; and the mobile shell.

(o/defprop --card-ink          "#1B1147")
(o/defprop --card-ink-04       "rgba(27,17,71,0.04)")
(o/defprop --card-ink-06       "rgba(27,17,71,0.06)")
(o/defprop --card-ink-18       "rgba(27,17,71,0.18)")
(o/defprop --card-ink-hover    "#3B2F66")
(o/defprop --card-text-muted   "#6B6486")
(o/defprop --card-text-faint   "#9B95B0")
(o/defprop --card-surface      "#F7F6EF")
(o/defprop --card-surface-92   "rgba(247,246,239,0.92)")
(o/defprop --card-bg           "#F7F6EF")
(o/defprop --sticky-yellow     "#F5E7A8")
(o/defprop --sticky-yellow-light "#FAF4DF")
(o/defprop --sticky-green      "#CFE0B8")
;; Sticky-note ink: the sage greens shared by the scratchpad sticky in the
;; web-app desktop and the browser-extension sidebar (paper is --sticky-yellow).
(o/defprop --note-sage         "#4A5D3F")
(o/defprop --note-sage-35      "rgba(74,93,63,0.35)")
(o/defprop --note-sage-55      "rgba(74,93,63,0.55)")
(o/defprop --note-sage-80      "rgba(74,93,63,0.80)")
(o/defprop --note-kraft        "#324025")
(o/defprop --note-kraft-10     "rgba(50,64,37,0.10)")
(o/defprop --note-kraft-18     "rgba(50,64,37,0.18)")
(o/defprop --card-cream        "#E4E2D2")
(o/defprop --card-cream-92     "rgba(228,226,210,0.92)")
(o/defprop --card-cream-60     "rgba(228,226,210,0.6)")
(o/defprop --card-border       "#E3DDF3")
(o/defprop --card-track        "#D8D4E3")
(o/defprop --card-violet-soft  "#EEEAFC")
(o/defprop --card-violet       "#6E5BCC")
(o/defprop --card-forest       "#2D3E2A")
(o/defprop --card-forest-06    "rgba(45,62,42,0.06)")
(o/defprop --card-forest-10    "rgba(45,62,42,0.10)")
(o/defprop --card-forest-14    "rgba(45,62,42,0.14)")
(o/defprop --card-forest-70    "rgba(45,62,42,0.7)")
(o/defprop --card-sage         "#9CAE85")
(o/defprop --card-sage-light   "#C2CDB1")
(o/defprop --card-sage-soft-15 "rgba(184,196,166,0.15)")
(o/defprop --card-sage-soft-20 "rgba(184,196,166,0.20)")

;; Task-card elevation — a task with a description rests as a raised, pressable
;; card so touch users can tell it holds detail without hovering. The resting
;; and hover shadows stack faux paper-edge layers under an ambient shadow; the
;; pressed shadow is shallow so the card reads as sunk into the surface while its
;; description extrudes underneath.
(o/defprop --task-card-surface "#FEFEFB")
;; The leading layer of each is cast upward (negative y): without it the card
;; only ever throws light downward and its top edge sits flat on the page.
(o/defprop --task-card-shadow
  "0 -3px 8px -6px rgba(45,62,42,0.16), 0 5px 0 -2px #F1F0E7, 0 6px 8px -5px rgba(45,62,42,0.12), 0 11px 0 -5px #E8E7DD, 0 12px 10px -7px rgba(45,62,42,0.10), 0 10px 24px -11px rgba(45,62,42,0.18)")
(o/defprop --task-card-shadow-hover
  "0 -5px 14px -8px rgba(45,62,42,0.22), 0 5px 0 -2px #F1F0E7, 0 6px 8px -5px rgba(45,62,42,0.12), 0 11px 0 -5px #E8E7DD, 0 14px 12px -7px rgba(45,62,42,0.12), 0 26px 46px -10px rgba(45,62,42,0.34)")
(o/defprop --task-card-shadow-pressed
  "0 2px 7px -3px rgba(45,62,42,0.14), 0 1px 2px rgba(0,0,0,0.04)")
(o/defprop --card-error        "#9A2828")
(o/defprop --card-error-12     "rgba(154,40,40,0.12)")
(o/defprop --card-warning      "#B07A00")
(o/defprop --card-warning-text "#7A5500")
(o/defprop --card-warning-12   "rgba(176,122,0,0.12)")
(o/defprop --card-info         "#3B5BA6")
(o/defprop --card-info-12      "rgba(59,91,166,0.12)")

(o/defprop --hai-black-1 "#120D06")
(o/defprop --hai-off-white-1 "#EAE6D9")
(o/defprop --hai-off-white-2 "#EEEBE0")
(o/defprop --hai-purple-1 "#796899")
(o/defprop --hai-grey-1 "#7B7B7B")
(o/defprop --hai-grey-2 "#aeaeae")

;; Open props shadows because they are gorgeous
(o/defprop --shadow-color "220 3% 15%")
(o/defprop --shadow-strength "1%")
(o/defprop --inner-shadow-highlight "inset 0 -.5px 0 0 #fff, inset 0 .5px 0 0 #0001")
(o/defprop --shadow-1-alpha "calc(var(--shadow-strength) + 9%)")
(o/defprop --shadow-1 "0 1px 2px -1px hsl(var(--shadow-color) / calc(var(--shadow-strength) + 9%))")
(o/defprop --shadow-2 "
0 3px 5px -2px hsl(var(--shadow-color) / calc(var(--shadow-strength) + 3%)),
0 7px 14px -5px hsl(var(--shadow-color) / calc(var(--shadow-strength) + 5%))")
(o/defprop --shadow-3 "
0 -1px 3px 0 hsl(var(--shadow-color) / calc(var(--shadow-strength) + 2%)),
0 1px 2px -5px hsl(var(--shadow-color) / calc(var(--shadow-strength) + 2%)),
0 2px 5px -5px hsl(var(--shadow-color) / calc(var(--shadow-strength) + 4%)),
0 4px 12px -5px hsl(var(--shadow-color) / calc(var(--shadow-strength) + 5%)),
0 12px 15px -5px hsl(var(--shadow-color) / calc(var(--shadow-strength) + 7%))")
(o/defprop --shadow-4 "
0 -2px 5px 0 hsl(var(--shadow-color) / calc(var(--shadow-strength) + 2%)),
0 1px 1px -2px hsl(var(--shadow-color) / calc(var(--shadow-strength) + 3%)),
0 2px 2px -2px hsl(var(--shadow-color) / calc(var(--shadow-strength) + 3%)),
0 5px 5px -2px hsl(var(--shadow-color) / calc(var(--shadow-strength) + 4%)),
0 9px 9px -2px hsl(var(--shadow-color) / calc(var(--shadow-strength) + 5%)),
0 16px 16px -2px hsl(var(--shadow-color) / calc(var(--shadow-strength) + 6%))")
(o/defprop --shadow-5 "
0 -1px 2px 0 hsl(var(--shadow-color) / calc(var(--shadow-strength) + 2%)),
0 2px 1px -2px hsl(var(--shadow-color) / calc(var(--shadow-strength) + 3%)),
0 5px 5px -2px hsl(var(--shadow-color) / calc(var(--shadow-strength) + 3%)),
0 10px 10px -2px hsl(var(--shadow-color) / calc(var(--shadow-strength) + 4%)),
0 20px 20px -2px hsl(var(--shadow-color) / calc(var(--shadow-strength) + 5%)),
0 40px 40px -2px hsl(var(--shadow-color) / calc(var(--shadow-strength) + 7%))")
(o/defprop --shadow-6 "
0 -1px 2px 0 hsl(var(--shadow-color) / calc(var(--shadow-strength) + 2%)),
0 3px 2px -2px hsl(var(--shadow-color) / calc(var(--shadow-strength) + 3%)),
0 7px 5px -2px hsl(var(--shadow-color) / calc(var(--shadow-strength) + 3%)),
0 12px 10px -2px hsl(var(--shadow-color) / calc(var(--shadow-strength) + 4%)),
0 22px 18px -2px hsl(var(--shadow-color) / calc(var(--shadow-strength) + 5%)),
0 41px 33px -2px hsl(var(--shadow-color) / calc(var(--shadow-strength) + 6%)),
0 100px 80px -2px hsl(var(--shadow-color) / calc(var(--shadow-strength) + 7%))")
(o/defprop --inner-shadow-0 "inset 0 0 0 1px hsl(var(--shadow-color) / calc(var(--shadow-strength) + 9%))")
(o/defprop --inner-shadow-1 "inset 0 1px 2px 0 hsl(var(--shadow-color) / calc(var(--shadow-strength) + 9%)), var(--inner-shadow-highlight)")
(o/defprop --inner-shadow-2 "inset 0 1px 4px 0 hsl(var(--shadow-color) / calc(var(--shadow-strength) + 9%)), var(--inner-shadow-highlight)")
(o/defprop --inner-shadow-3 "inset 0 2px 8px 0 hsl(var(--shadow-color) / calc(var(--shadow-strength) + 9%)), var(--inner-shadow-highlight)")
(o/defprop --inner-shadow-4 "inset 0 2px 14px 0 hsl(var(--shadow-color) / calc(var(--shadow-strength) + 9%)), var(--inner-shadow-highlight)")

(o/defprop --property-1  "#E8F1E3")
(o/defprop --property-2  "#F9F8F4")
(o/defprop --property-3  "#D9E0CE")
(o/defprop --property-4  "#3d4636")
(o/defprop --property-5  "#6e5fdb")
(o/defprop --property-6  "#fbf8f0")
(o/defprop --property-7  "#1f2418")
(o/defprop --property-8  "#6e7560")
(o/defprop --property-9  "#f6e89a")
(o/defprop --property-10 "#d6e2bf")
(o/defprop --property-11 "#F5E7A8")
(o/defprop --property-12 "#F0D389")
(o/defprop --property-13 "#b13a30")
(o/defprop --property-14 "#992c24")
(o/defprop --property-15 "#6B7560")
(o/defprop --property-16 "#e0e0e0")
(o/defprop --property-17 "#fafafa")
(o/defprop --property-18 "#b0b0b0")
(o/defprop --property-19 "#f5f5f5")
(o/defprop --property-20 "#6366f1")
(o/defprop --property-21 "#eef2ff")
(o/defprop --property-22 "#e8e8ff")
(o/defprop --property-23 "#dc2626")
(o/defprop --property-24 "#dcfce7")
(o/defprop --property-25 "#fef3c7")
(o/defprop --property-26 "#fee2e2")
(o/defprop --property-27 "#f3f4f6")
(o/defprop --property-28 "#166534")
(o/defprop --property-29 "#92400e")
(o/defprop --property-30 "#991b1b")
(o/defprop --property-31 "#374151")
(o/defprop --property-32 "#DAF8F3")
(o/defprop --property-33 "#45DCC1")
(o/defprop --property-34 "#2DB89E")
(o/defprop --property-35 "#ECE6FF")
(o/defprop --property-36 "#B47E00")
(o/defprop --property-37 "#3564FF")
(o/defprop --property-38 "#DFF5FF")
(o/defprop --property-39 "#F0F0F0")
(o/defprop --property-40 "#FFF4CC")
(o/defprop --property-41 "#E6F7EC")
(o/defprop --property-42 "#2563eb")
